import { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Input, Button, Row, Col } from "reactstrap";
import { createCapturePoint, updateCapturePoint } from "../../utils/driveThruApi";
import { successToast, errorToast } from "../../shared/Toast";
import { C } from "../reporting/reportingStyles";
import StopBoxEditor from "./StopBoxEditor";

const headerStyle = { borderBottom: `2px solid ${C.borderLight}` };
const bodyStyle = { padding: 24, fontFamily: "'Inter', sans-serif" };
const footerStyle = { borderTop: `1px solid ${C.borderLight}`, gap: 8 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 6, display: "block" };
const inputStyle = { borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 13, padding: "8px 12px", color: C.text };
const coralBtnStyle = { background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13 };
const ghostBtnStyle = { background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 };

const blankForm = {
  type: "",
  cameraBrand: "",
  cameraIp: "",
  cameraHttpPort: 80,
  cameraRtspPort: 554,
  cameraChannel: 1,
  cameraUser: "",
  cameraPass: "",
  plateRegion: "",
  isActive: true,
  testMode: false,
  testImageUrl: "",
  stopBoxRegion: null,
};

// Create/edit a capture point (ORDER or PICKUP camera) under a lane.
// `capturePoint` null = create mode. brands/types come from the backend meta.
const CapturePointModal = ({ isOpen, toggle, onSaved, capturePoint, laneId, brands = [], types = [] }) => {
  const editing = !!capturePoint;
  const hasPassword = !!capturePoint?.hasPassword;
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (editing) {
      setForm({
        type: capturePoint.type || "",
        cameraBrand: capturePoint.cameraBrand || brands[0] || "",
        cameraIp: capturePoint.cameraIp || "",
        cameraHttpPort: capturePoint.cameraHttpPort ?? 80,
        cameraRtspPort: capturePoint.cameraRtspPort ?? 554,
        cameraChannel: capturePoint.cameraChannel ?? 1,
        cameraUser: capturePoint.cameraUser || "",
        cameraPass: "", // write-only — never prefilled
        plateRegion: capturePoint.plateRegion || "",
        isActive: capturePoint.isActive ?? true,
        testMode: capturePoint.testMode ?? false,
        testImageUrl: capturePoint.testImageUrl || "",
        stopBoxRegion: capturePoint.stopBoxRegion || null,
      });
    } else {
      setForm({ ...blankForm, cameraBrand: brands[0] || "", type: types[0] || "" });
    }
  }, [isOpen, capturePoint, editing, brands, types]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.type) return errorToast("Capture point type is required");
    if (!form.cameraIp.trim()) return errorToast("Camera IP / host is required");
    for (const port of [form.cameraHttpPort, form.cameraRtspPort]) {
      if (Number(port) < 1 || Number(port) > 65535) return errorToast("Ports must be between 1 and 65535");
    }
    if (Number(form.cameraChannel) < 1) return errorToast("Channel must be 1 or greater");
    if (form.testMode && !form.testImageUrl.trim()) return errorToast("Test image URL is required in test mode");

    const payload = {
      type: form.type,
      cameraBrand: form.cameraBrand,
      cameraIp: form.cameraIp.trim(),
      cameraHttpPort: Number(form.cameraHttpPort),
      cameraRtspPort: Number(form.cameraRtspPort),
      cameraChannel: Number(form.cameraChannel),
      cameraUser: form.cameraUser || "",
      plateRegion: form.plateRegion || "",
      isActive: form.isActive,
      testMode: form.testMode,
      testImageUrl: form.testMode ? form.testImageUrl.trim() : "",
      stopBoxRegion: form.stopBoxRegion ?? null,
    };
    // Only send the password when the admin actually typed one — blank keeps the
    // existing stored password unchanged.
    if (form.cameraPass) payload.cameraPass = form.cameraPass;
    if (!editing) payload.laneId = laneId;

    setSaving(true);
    const result = editing
      ? await updateCapturePoint(capturePoint.id, payload)
      : await createCapturePoint(payload);
    setSaving(false);
    if (result) {
      successToast(editing ? "Capture point updated" : "Capture point created");
      onSaved();
      toggle();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle} style={headerStyle}>
        <span style={{ fontWeight: 700, color: C.text }}>{editing ? "Edit Capture Point" : "Add Capture Point"}</span>
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody style={bodyStyle}>
          <Row>
            <Col md={6}>
              <FormGroup>
                <label style={labelStyle}>Type</label>
                <Input type="select" style={inputStyle} value={form.type} onChange={(e) => setField("type", e.target.value)} required>
                  <option value="">Select type</option>
                  {types.map((t) => (
                    <option key={t} value={t}>
                      {t === "ORDER" ? "Order point (greet customer)" : t === "PICKUP" ? "Pickup point (match order)" : t}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <label style={labelStyle}>Camera Brand</label>
                <Input type="select" style={inputStyle} value={form.cameraBrand} onChange={(e) => setField("cameraBrand", e.target.value)}>
                  {brands.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <label style={labelStyle}>Camera IP / Host</label>
                <Input style={inputStyle} value={form.cameraIp} onChange={(e) => setField("cameraIp", e.target.value)} placeholder="192.168.1.50" required />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <label style={labelStyle}>HTTP Port</label>
                <Input type="number" style={inputStyle} min={1} max={65535} value={form.cameraHttpPort} onChange={(e) => setField("cameraHttpPort", e.target.value)} />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <label style={labelStyle}>RTSP Port</label>
                <Input type="number" style={inputStyle} min={1} max={65535} value={form.cameraRtspPort} onChange={(e) => setField("cameraRtspPort", e.target.value)} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <FormGroup>
                <label style={labelStyle}>Channel</label>
                <Input type="number" style={inputStyle} min={1} value={form.cameraChannel} onChange={(e) => setField("cameraChannel", e.target.value)} />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <label style={labelStyle}>Plate Region</label>
                <Input style={inputStyle} value={form.plateRegion} onChange={(e) => setField("plateRegion", e.target.value)} placeholder="sa (default)" />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <label style={labelStyle}>Camera Username</label>
                <Input style={inputStyle} value={form.cameraUser} onChange={(e) => setField("cameraUser", e.target.value)} placeholder="admin" autoComplete="off" />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <label style={labelStyle}>Camera Password</label>
            <Input
              type="password"
              style={inputStyle}
              value={form.cameraPass}
              onChange={(e) => setField("cameraPass", e.target.value)}
              placeholder={hasPassword ? "•••••••• (set — leave blank to keep)" : "Enter camera password"}
              autoComplete="new-password"
            />
            <small style={{ color: C.textLight, fontSize: 11 }}>
              {hasPassword
                ? "A password is already saved. Leave blank to keep it, or type a new one to replace it."
                : "Stored encrypted; never shown again after saving."}
            </small>
          </FormGroup>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: C.text, fontWeight: 600 }}>
            <Input type="checkbox" checked={form.isActive} onChange={(e) => setField("isActive", e.target.checked)} style={{ position: "static", margin: 0 }} />
            Active
          </label>

          {/* Test camera: bypass the real camera and use a fixed image for testing. */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.borderLight}` }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: C.text, fontWeight: 600 }}>
              <Input type="checkbox" checked={form.testMode} onChange={(e) => setField("testMode", e.target.checked)} style={{ position: "static", margin: 0 }} />
              Test mode (use a fixed image instead of the camera)
            </label>
            {form.testMode && (
              <FormGroup className="mt-2">
                <label style={labelStyle}>Test Image URL</label>
                <Input
                  style={inputStyle}
                  value={form.testImageUrl}
                  onChange={(e) => setField("testImageUrl", e.target.value)}
                  placeholder="https://…/car-with-plate.jpg"
                />
                <small style={{ color: C.textLight, fontSize: 11 }}>
                  The POS fetches this image (ignoring the camera IP) and sends it for recognition.
                </small>
              </FormGroup>
            )}
          </div>

          {/* Stop box: the ROI the on-prem detector watches to decide a car has stopped. */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.borderLight}` }}>
            <StopBoxEditor
              value={form.stopBoxRegion}
              onChange={(region) => setField("stopBoxRegion", region)}
              capturePointId={capturePoint?.id}
            />
          </div>
        </ModalBody>
        <ModalFooter style={footerStyle}>
          <Button type="button" onClick={toggle} style={ghostBtnStyle}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving} style={coralBtnStyle}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CapturePointModal;
